import React, {
	useCallback,
	useImperativeHandle,
	ForwardRefRenderFunction,
	useState,
	useRef,
} from "react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@alm/flow-ds/components";
import classnames from "classnames";
import "react-circular-progressbar/dist/styles.css";
import UploadItem, { UploadItemProps } from "./UploadItem";
import { useTranslation } from "react-i18next";

type Props = Omit<UploadItemProps, "file">;

interface RefMethods {
	openFileBrowser: () => void;
}

interface MediaFile {
	file: File;
	complete: boolean;
}

const MediaUpload: ForwardRefRenderFunction<RefMethods, Props> = (
	{ onUploadStart, onUploadComplete, onUploadSuccess },
	ref
) => {
	const activeCount = useRef(0);
	const { t } = useTranslation();
	const [media, setMedia] = useState<MediaFile[]>([]);

	const onDrop = useCallback(
		(files: File[]) => {
			setMedia([
				...media,
				...files.map(file => ({ complete: false, file })),
			]);
		},
		[media]
	);

	const handleUploadStart = useCallback(() => {
		if (onUploadStart && activeCount.current === 0) onUploadStart();
		activeCount.current = activeCount.current + 1;
	}, [onUploadStart]);

	const handleUploadComplete = useCallback(() => {
		if (onUploadComplete && activeCount.current === 1) onUploadComplete();
		activeCount.current = activeCount.current - 1;
	}, [onUploadComplete]);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop,
	});

	useImperativeHandle(ref, () => ({
		openFileBrowser: open,
	}));

	return (
		<>
			<div
				{...getRootProps()}
				className={classnames(
					"upload__dropzone",
					isDragActive ? "upload__dropzone--dragOver" : null
				)}>
				<input {...getInputProps()} />

				{isDragActive ? (
					<div className="upload__dropzone-content">
						<Icon
							type="images"
							className="upload__dropzone-icon"
							size="3x"
							weight="d"
						/>
						<p className="mt-2 mb-0">
							{t("elements.media.dropzoneActive")}
						</p>
					</div>
				) : (
					<div className="upload__dropzone-content">
						<Icon
							type="images"
							className="upload__dropzone-icon"
							size="3x"
							weight="d"
						/>
						<p className="mt-2 mb-0">
							{t("elements.media.dropzoneLabel")}
						</p>
					</div>
				)}
			</div>

			{media.map((item, index) => (
				<UploadItem
					key={`item-${index}`}
					file={item.file}
					onUploadStart={handleUploadStart}
					onUploadComplete={handleUploadComplete}
					onUploadSuccess={onUploadSuccess}
				/>
			))}
		</>
	);
};

export default React.forwardRef(MediaUpload);
